import request from 'superagent/lib/client';
import apiUrlProvider from './ApiURL';

export default class UtapiMetrics {
    constructor() {
        this.credentials = JSON.parse(localStorage.getItem('scality-credentials')) || '';
    }

    static _prepareTimeRange(startDate, endDate) {
        const timeRange = [startDate.valueOf()];
        if (endDate) {
            timeRange.push(endDate.valueOf());
        }

        return timeRange;
    }


    _doAPIRequest(url, body, cb) {
        return new Promise((resolve, reject) => {
            request
                .post(url)
                .set('x-access-token', this.credentials)
                .send(body)
                .end((err, response) => {
                    if (err) {
                        reject(err);
                    } else {
                        const resp = JSON.parse(response.text);
                        resolve(cb(resp));
                    }
                });
        });
    }

    _getSingleResourceMetrics(url, body, startDate, endDate) {
        const timeRange = UtapiMetrics._prepareTimeRange(startDate, endDate);

        return this._doAPIRequest(
            url,
            { timeRange, ...body },
            (resp) => {
                const { storageUtilized, numberOfObjects, incomingBytes, outgoingBytes, operations } = resp[0];
                return { storageUtilized, numberOfObjects, incomingBytes, outgoingBytes, operations };
            }
        );
    }

    _getSingleResourceMetricsHistory(url, body, startDate, endDate) {
        const timeRange = UtapiMetrics._prepareTimeRange(startDate, endDate);

        return this._doAPIRequest(
            url,
            { timeRange, ...body },
            (resp) =>
                resp.map(elem => {
                    const { timeRange, storageUtilized, incomingBytes, outgoingBytes, operations } = elem[0];
                    return {
                        timeRange: timeRange[0],
                        storageUtilized: storageUtilized[1],
                        incomingBytes,
                        outgoingBytes,
                        operations,
                    };
                })
        );
    }

    /**
    * Retrieve metrics for the given bucket.
    *
    * @param {Object} bucket - Name of the bucket to retrieve metrics for.
    * @param {Object} startDate - Moment object indicating the metrics start date.
    * @param {Object} endDate - Moment object indicating the metrics end date.
    *
    * @returns {Object} - An object with the following properties
    *                     - {number[]} storageUtilized: The amount of storage used (in bytes).
    *                     - {number[]} numberOfObjects: The number of objects stored.
    *                     - {number} incomingBytes: The amount of data received (in bytes).
    *                     - {number} outgoingBytes: The amount of data sent (in bytes).
    *                     - {Object} operations: The number of operations, for each operation type.
    */
    async getBucketMetrics(bucket, startDate, endDate) {
        const metrics = await this._getSingleResourceMetrics(
            `${apiUrlProvider.apiUrl()}/utapi/buckets`,
            { buckets: [bucket] },
            startDate,
            endDate,
        );

        return { metrics, downsampledMetrics: false };
    }

    /**
    * Retrieve metrics for the given account.
    *
    * @param {Object} account - Object containing information about an IAM account.
    * @param {string} account.accountId - The account ID.
    * @param {string} account.accountName - The account name.
    * @param {Object} startDate - Moment object indicating the metrics start date.
    * @param {Object} endDate - Moment object indicating the metrics end date.
    *
    * @returns {Object} - An object with the following properties
    *                     - {number[]} storageUtilized: The amount of storage used (in bytes).
    *                     - {number[]} numberOfObjects: The number of objects stored.
    *                     - {number} incomingBytes: The amount of data received (in bytes).
    *                     - {number} outgoingBytes: The amount of data sent (in bytes).
    *                     - {Object} operations: The number of operations, for each operation type.
    */
    async getAccountMetrics(account, startDate, endDate) {
        const metrics = await this._getSingleResourceMetrics(
            `${apiUrlProvider.apiUrl()}/utapi/accounts`,
            { accounts: [account.accountId] },
            startDate,
            endDate,
        );

        return { metrics, downsampledMetrics: false };
    }

    /**
    * Retrieve metrics for the s3 service.
    *
    * @param {Object} startDate - Moment object indicating the metrics start date.
    * @param {Object} endDate - Moment object indicating the metrics end date.
    *
    * @returns {Object} - An object with the following properties
    *                     - {number[]} storageUtilized: The amount of storage used (in bytes).
    *                     - {number[]} numberOfObjects: The number of objects stored.
    *                     - {number} incomingBytes: The amount of data received (in bytes).
    *                     - {number} outgoingBytes: The amount of data sent (in bytes).
    *                     - {Object} operations: The number of operations, for each operation type.
    */
    async getSystemMetrics(startDate, endDate) {
        const metrics = await this._getSingleResourceMetrics(
            `${apiUrlProvider.apiUrl()}/utapi/`,
            { service: ['s3'] },
            startDate,
            endDate,
        );

        return { metrics, downsampledMetrics: false };
    }

    /**
    * Retrieve metrics for the given array of buckets.
    *
    * @param {string[]} buckets - Array of bucket names to retrieve metrics for.
    * @param {Object} startDate - Moment object indicating the metrics start date.
    * @param {Object} endDate - Moment object indicating the metrics end date.
    *
    * @returns {Array} - An array where each element is an object with the following properties:
    *                    - {string} bucketName: Bucket name.
    *                    - {number} storageUtilized: The amount of storage used (in bytes) at the end date.
    *                    - {number} incomingBytes: The amount of data received (in bytes).
    *                    - {number} outgoingBytes: The amount of data sent (in bytes).
    *                    Note that the `numberOfObjects` is not returned as it not used by the users of the API.
    */
    async getMultipleBucketsMetrics(buckets, startDate, endDate) {
        const timeRange = UtapiMetrics._prepareTimeRange(startDate, endDate);

        const metrics = await this._doAPIRequest(
            `${apiUrlProvider.apiUrl()}/utapi/buckets`,
            {
                buckets,
                timeRange,
            },
            (resp) =>
                resp.map(elem => {
                    const { bucketName, storageUtilized, incomingBytes, outgoingBytes } = elem;
                    return {
                        bucketName,
                        storageUtilized: storageUtilized[1],
                        incomingBytes,
                        outgoingBytes,
                    };
                })
        );

        return { metrics, downsampledMetrics: false };
    }

    /**
    * Retrieve metrics for the given array of accounts.
    *
    * @param {Object[]} accounts - Array where each element is an object with the following properties:
                                    - {string} account.accountId - Account ID.
                                    - {string} account.accountName - Account name.
    * @param {Object} startDate - Moment object indicating the metrics start date.
    * @param {Object} endDate - Moment object indicating the metrics end date.
    *
    * @returns {Array} - An array where each element is an object with the following properties:
    *                    - {string} accountId: Account ID.
    *                    - {number} storageUtilized: The amount of storage used (in bytes) at the end date.
    *                    - {number} incomingBytes: The amount of data received (in bytes).
    *                    - {number} outgoingBytes: The amount of data sent (in bytes).
    *                    Note that the `numberOfObjects` is not returned as it not used by the users of the API.
    */
    async getMultipleAccountsMetrics(accounts, startDate, endDate) {
        const timeRange = UtapiMetrics._prepareTimeRange(startDate, endDate);

        const metrics = await this._doAPIRequest(
            `${apiUrlProvider.apiUrl()}/utapi/accounts`,
            {
                accounts: accounts.map(item => item.accountId),
                timeRange,
            },
            (resp) =>
                resp.map(elem => {
                    const { accountId, storageUtilized, incomingBytes, outgoingBytes } = elem;
                    return {
                        accountId,
                        storageUtilized: storageUtilized[1],
                        incomingBytes,
                        outgoingBytes,
                    };
                })
        );

        return { metrics, downsampledMetrics: false };
    }

    /**
    * Retrieve metrics history for the given bucket.
    *
    * @param {Object} bucket - Name of the bucket to retrieve metrics for.
    * @param {string} interval - Interval to be used for metrics history.
    * @param {Object} startDate - Moment object indicating the metrics start date.
    * @param {Object} endDate - Moment object indicating the metrics end date.
    *
    * @returns {Array} - An array where each element is an object with the following properties:
    *                    - {number} timeRange: The start date.
    *                    - {number} storageUtilized: The amount of storage used (in bytes) at the end date.
    *                    - {number} incomingBytes: The amount of data received (in bytes).
    *                    - {number} outgoingBytes: The amount of data sent (in bytes).
    *                    - {Object} operations: The number of operations, for each operation type.
    *                    Note that the `numberOfObjects` is not returned as it not used by the users of the API.
    */
    async getBucketMetricsHistory(bucket, interval, startDate, endDate) {
        const metrics = await this._getSingleResourceMetricsHistory(
            `${apiUrlProvider.apiUrl()}/utapi/history/${interval}/buckets`,
            { buckets: [bucket] },
            startDate,
            endDate,
        );

        return { metrics, downsampledMetrics: false };
    }

    /**
    * Retrieve metrics history for the given account.
    *
    * @param {Object} account - Object containing information about an IAM account.
    * @param {string} account.accountId - The account ID.
    * @param {string} account.accountName - The account name.
    * @param {string} interval - Interval to be used for metrics history.
    * @param {Object} startDate - Moment object indicating the metrics start date.
    * @param {Object} endDate - Moment object indicating the metrics end date.
    *
    * @returns {Array} - An array where each element is an object with the following properties:
    *                    - {number} timeRange: The start date.
    *                    - {number} storageUtilized: The amount of storage used (in bytes) at the end date.
    *                    - {number} incomingBytes: The amount of data received (in bytes).
    *                    - {number} outgoingBytes: The amount of data sent (in bytes).
    *                    - {Object} operations: The number of operations, for each operation type.
    *                    Note that the `numberOfObjects` is not returned as it not used by the users of the API.
    */
    async getAccountMetricsHistory(account, interval, startDate, endDate) {
        const metrics = await this._getSingleResourceMetricsHistory(
            `${apiUrlProvider.apiUrl()}/utapi/history/${interval}/accounts`,
            { accounts: [account.accountId] },
            startDate,
            endDate,
        );

        return { metrics, downsampledMetrics: false };
    }

    /**
    * Retrieve metrics history for the s3 service.
    *
    * @param {string} interval - Interval to be used for metrics history.
    * @param {Object} startDate - Moment object indicating the metrics start date.
    * @param {Object} endDate - Moment object indicating the metrics end date.
    *
    * @returns {Array} - An array where each element is an object with the following properties:
    *                    - {number} timeRange: The start date.
    *                    - {number} storageUtilized: The amount of storage used (in bytes) at the end date.
    *                    - {number} incomingBytes: The amount of data received (in bytes).
    *                    - {number} outgoingBytes: The amount of data sent (in bytes).
    *                    - {Object} operations: The number of operations, for each operation type.
    *                    Note that the `numberOfObjects` is not returned as it not used by the users of the API.
    */
    async getSystemMetricsHistory(interval, startDate, endDate) {
        const metrics = await this._getSingleResourceMetricsHistory(
            `${apiUrlProvider.apiUrl()}/utapi/history/${interval}/`,
            { service: ['s3'] },
            startDate,
            endDate,
        );

        return { metrics, downsampledMetrics: false };
    }

    /**
    * Retrieve metrics history for the buckets with the most used storage.
    *
    * @param {string} interval - Interval to be used for metrics history.
    * @param {Object} startDate - Moment object indicating the metrics start date.
    * @param {Object} endDate - Moment object indicating the metrics end date.
    * @param {Array} topBuckets - Array of bucket names contains the buckets with the most storage utilization
    *
    * @returns {Array} - A 2-dimensional array.
    *                    The outer array contains metrics each period (e.g. day) in the given interval.
    *                    Each element inner array contains metrics for multiple buckets:
    *                    Each element of the inner array is an object with the following properties:
    *                    - {string} bucketName: Bucket name.
    *                    - {number} timeRange: The end date.
    *                    - {number} storageUtilized: The amount of storage used (in bytes) at the end date.
    */
    async getMultipleBucketsHistory(interval, startDate, endDate, topBuckets) {
        const timeRange = UtapiMetrics._prepareTimeRange(startDate, endDate);

        if (topBuckets === undefined) {
            // eslint-disable-next-line no-param-reassign
            topBuckets = await this.getTopBuckets(startDate, endDate);
        }

        const metrics = await this._doAPIRequest(
            `${apiUrlProvider.apiUrl()}/utapi/history/buckets/all/${interval}`,
            {
                topBuckets,
                timeRange,
            },
            (resp) =>
                    resp.map(historyElem =>
                        historyElem.map(bucketElem => {
                            const { bucketName, timeRange, storageUtilized } = bucketElem;
                            return {
                                bucketName,
                                timeRange: timeRange[1],
                                storageUtilized: storageUtilized[1],
                            };
                        })
                    ),
        );

        return { metrics, downsampledMetrics: false };
    }

    getTopBuckets(startDate, endDate) {
        const timeRange = UtapiMetrics._prepareTimeRange(startDate, endDate);

        return this._doAPIRequest(
            `${apiUrlProvider.apiUrl()}/utapi/topbuckets`,
            { timeRange },
            (topBuckets) => topBuckets
        );
    }
}



/** WEBPACK FOOTER **
 ** ./src/utils/UtapiMetrics.js
 **/